import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { Skeleton } from '~/components/ui/skeleton';
import { useECharts } from '~/hooks/use-echarts';
import { resolveThemeColor } from '~/utils/chart-colors';
import { cn } from '~/utils/cn';

import type { TestVsControlRevenuePoint } from '../../types/experiment.types';

interface Props {
  points: TestVsControlRevenuePoint[];
  /** Display label for the Test series (defaults to "Test"). */
  testLabel?: string | undefined;
  /** Display label for the Control series (defaults to "Control"). */
  controlLabel?: string | undefined;
  isLoading?: boolean | undefined;
  className?: string | undefined;
}

/**
 * "Test vs. Control Revenue" grouped bar chart. Two series per week: Test
 * (primary blue) and Control (success green). Currency formatting on both the
 * Y-axis and tooltips, with a circular-dot legend at the top right.
 */
export function ExperimentTestVsControlBarChart({
  points,
  testLabel = 'Test',
  controlLabel = 'Control',
  isLoading,
  className,
}: Props) {
  const option = useMemo<EChartsOption>(() => {
    const primaryColor = resolveThemeColor('--primary');
    const successColor = resolveThemeColor('--success');
    const axisColor = resolveThemeColor('--muted-foreground');
    const gridLineColor = resolveThemeColor('--border');

    const xAxisCategories = points.map((p) => p.weekLabel);

    return {
      grid: { left: 16, right: 16, top: 32, bottom: 32, containLabel: true },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (val: unknown) =>
          typeof val === 'number' ? formatCurrencyTooltip(val) : String(val),
      },
      legend: {
        show: true,
        top: 0,
        right: 0,
        textStyle: { color: axisColor, fontSize: 11 },
        itemWidth: 8,
        itemHeight: 8,
        icon: 'circle',
      },
      xAxis: {
        type: 'category',
        data: xAxisCategories,
        axisLine: { lineStyle: { color: gridLineColor } },
        axisTick: { show: false },
        axisLabel: { color: axisColor, fontSize: 12 },
      },
      yAxis: {
        type: 'value',
        position: 'left',
        axisLabel: { color: axisColor, fontSize: 12, formatter: formatCurrencyAxis },
        splitLine: { lineStyle: { color: gridLineColor, type: 'dashed' } },
      },
      series: [
        {
          type: 'bar',
          name: testLabel,
          data: points.map((p) => p.test),
          itemStyle: { color: primaryColor },
          barMaxWidth: 24,
        },
        {
          type: 'bar',
          name: controlLabel,
          data: points.map((p) => p.control),
          itemStyle: { color: successColor },
          barMaxWidth: 24,
        },
      ],
    } as EChartsOption;
  }, [points, testLabel, controlLabel]);

  const chartRef = useECharts(option);

  return (
    <div className={cn('relative h-64 w-full min-w-0', className)}>
      <div ref={chartRef} className="absolute inset-0" />
      {isLoading ? <Skeleton className="absolute inset-0" /> : null}
    </div>
  );
}

/** Y-axis tick label — full dollar amount (e.g. `$14,000`). */
function formatCurrencyAxis(val: number): string {
  return `$${val.toLocaleString('en-US')}`;
}

/** Tooltip line — full dollar amount (e.g. `$11,200`). */
function formatCurrencyTooltip(val: number): string {
  return `$${val.toLocaleString('en-US')}`;
}
